import React from "react";
import * as Yup from "yup";
import * as R from "ramda";
import _ from "lodash";
import moment from "moment-timezone";
import { Formik, FormikValues } from "formik";

import { TextField, Button, FilePicker, DatePicker } from "..";
import { MACHINE_CONSUMPT_FORM_FIELDS } from "../../utils/constants";
import { replaceWord, writeXlsxFile } from "../../utils/functions";
import { MachineConsumptionFormProps } from "../../utils/types/components";

const INPUT_YUP_DEFINITIONS = MACHINE_CONSUMPT_FORM_FIELDS.reduce(
  (acc, { name }) => ({
    ...acc,
    [name]: Yup.number().typeError("Solo digitos").required("Campo requerido"),
  }),
  {},
);

const INPUT_INIT_VALUES = MACHINE_CONSUMPT_FORM_FIELDS.reduce(
  (acc, item) => ({ ...acc, [item.name]: "" }),
  {},
);

const VALIDATION_SCHEMA = Yup.object().shape({
  ...INPUT_YUP_DEFINITIONS,
  baseDate: Yup.date().typeError("Solo fecha").required("Campo requerido"),
});

/**
 * MachineConsumptionForm
 */

export const MachineConsumptionForm = ({
  onSubmit,
  defaultValues,
}: MachineConsumptionFormProps): React.ReactElement => {
  const formatLabel = R.pipe(R.trim, R.toLower, (value) =>
    typeof value === "string"
      ? replaceWord(
          [
            { from: /á/g, to: "a" },
            { from: /é/g, to: "e" },
            { from: /í/g, to: "i" },
            { from: /ó/g, to: "o" },
            { from: /ú/g, to: "u" },
          ],
          value,
        )
      : value,
  );

  const getName: (any: any) => string = R.prop("name");
  const findLabel = (label) => R.find(R.propEq("label", label));
  const findNameByLabel = (label) =>
    R.pipe(
      findLabel(formatLabel(label)),
      getName,
    )(
      R.map(
        ({ label: lbl, name }) => ({ label: formatLabel(lbl), name }),
        MACHINE_CONSUMPT_FORM_FIELDS,
      ),
    );

  const setDataByXlsx = (cb) => (data: any[]) => {
    data = data[0];
    const newD = R.keys(data).reduce(
      (acc, item) => ({ ...acc, [findNameByLabel(item)]: data[item] }),
      {},
    );
    cb(newD, true);
  };

  const getValuesByArray = (
    newValues: { id: string; value: number | string }[] = [],
    currentValues: Record<string, number | string> = {},
  ) =>
    newValues.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]:
          acc[item.id] === "" || _.isNil(acc[item.id])
            ? item.value
            : acc[item.id],
      }),
      currentValues,
    );

  return (
    <Formik
      initialValues={{
        baseDate: moment(),
        ...INPUT_INIT_VALUES,
      }}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={onSubmit}>
      {({
        values,
        handleChange,
        errors,
        isValid,
        handleSubmit,
        setValues,
        setFieldValue,
      }: FormikValues) => (
        <>
          <div>
            <div
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "flex-end",
                marginTop: 10,
                marginBottom: 20,
              }}>
              <Button
                category="default"
                onClick={() =>
                  setValues(getValuesByArray(defaultValues, values))
                }>
                Rellenar
              </Button>

              <FilePicker
                title="Cargar excel"
                style={{ marginLeft: 10 }}
                onChange={(d) =>
                  setDataByXlsx((excelValues) =>
                    setValues({ ...values, ...excelValues }),
                  )(d)
                }
              />
              <Button
                style={{ marginLeft: 10 }}
                category="default"
                onClick={() =>
                  writeXlsxFile({
                    data: [
                      MACHINE_CONSUMPT_FORM_FIELDS.map(({ label }) => label),
                    ],
                    fileName: "formato",
                    sheetName: "Formato",
                  })
                }>
                Formato Excel
              </Button>
            </div>
            {MACHINE_CONSUMPT_FORM_FIELDS.map(({ name, label }) => (
              <TextField
                key={name}
                id={`text-field-${name}`}
                label={label}
                value={values[name]}
                onChange={handleChange(name)}
                error={Boolean(errors[name])}
                helperText={errors[name]}
                type="number"
              />
            ))}
          </div>
          <div>
            <DatePicker
              margin="normal"
              id="text-field-base-date"
              label="Fecha base"
              defaultValue={values.baseDate}
              value={values.baseDate}
              onChange={(v) => setFieldValue("baseDate", v)}
            />
          </div>
          <Button
            style={{ marginTop: "20px" }}
            onClick={handleSubmit}
            disabled={!isValid}>
            Aceptar
          </Button>
        </>
      )}
    </Formik>
  );
};
